import type {PluginListenerHandle} from '@capacitor/core';

export type StreamProtocol = 'hls' | 'dash' | 'progressive';
export type PlaybackKind = 'audio' | 'video';
export type RepeatMode = 'off' | 'all' | 'one';
export type CarSurface = 'carplay' | 'androidAuto';
export type SiriAuthorizationStatus =
  | 'authorized'
  | 'denied'
  | 'notDetermined'
  | 'restricted'
  | 'unsupported'
  | 'unknown';
export type RemotePlaybackCommand =
  | 'play'
  | 'pause'
  | 'stop'
  | 'next'
  | 'previous'
  | 'seekTo'
  | 'skipToIndex'
  | 'setRepeatMode'
  | 'setShuffle';

export interface NativePlaybackConfig {
  apiBaseUrl: string;
  storageBaseUrl: string;
  accessToken?: string;
  userId?: number;
  language?: string;
}

export interface NativePlaybackLanguageOptions {
  language?: string;
  locale?: string;
  siteName?: string;
  site_name?: string;
  translations?: Record<string, string>;
  i18n?: {
    lines?: Record<string, string>;
  };
}

export interface PlaybackItem {
  id: string | number;
  groupId?: string | number;
  src: string;
  streamProtocol: StreamProtocol;
  playbackKind: PlaybackKind;
  sourcePlaybackKind?: PlaybackKind;
  audioOnly?: boolean;
  title: string;
  artist?: string;
  album?: string;
  artworkUrl?: string;
  durationMs?: number;
  isLive?: boolean;
  extras?: Record<string, unknown>;
}

export interface QueueReplaceOptions {
  items: PlaybackItem[];
  startIndex: number;
  autoPlay: boolean;
}

export interface QueueAppendOptions {
  items: PlaybackItem[];
  afterCurrent: boolean;
}

export interface QueueAddOptions {
  items: PlaybackItem[];
  index: number;
}

export interface QueueRemoveItem {
  id: string | number;
  groupId?: string | number;
}

export interface QueueRemoveOptions {
  items: QueueRemoveItem[];
}

export interface QueueSnapshot {
  items: PlaybackItem[];
  currentIndex: number;
  shuffling: boolean;
  repeatMode: RepeatMode;
  controlsLocked: boolean;
}

export interface PlaybackState {
  isReady: boolean;
  isPlaying: boolean;
  isBuffering: boolean;
  isCurrentItemVideo?: boolean;
  isVideoPresentationActive?: boolean;
  currentIndex: number;
  positionMs: number;
  durationMs: number;
  volume: number;
  muted: boolean;
  rate: number;
  shuffling: boolean;
  repeatMode: RepeatMode;
  controlsLocked: boolean;
  currentItem?: PlaybackItem;
}

export interface PlaybackStateChangedEvent {
  state: PlaybackState;
}

export interface QueueChangedEvent {
  queue: QueueSnapshot;
}

export interface RemoteCommandReceivedEvent {
  command: RemotePlaybackCommand;
  payload?: Record<string, unknown>;
}

export interface CarConnectionChangedEvent {
  connected: boolean;
  surface?: CarSurface;
}

export type LimitReachedIdentifier =
  | 'previewLimitReached'
  | 'listenQuotaReached'
  | 'nextQuotaReached'
  | 'previousQuotaReached';
export type LimitReachedKind = LimitReachedIdentifier;
export type LimitPromptType = 'createAccount' | 'upgradePlan';
export type LimitReachedSource = 'guest' | 'user' | 'none';
export type LimitReachedLimitType =
  | 'preview'
  | 'listen24h'
  | 'nextPerHour'
  | 'prevPerHour';

export interface LimitReachedEvent {
  identifier: LimitReachedIdentifier;
  kind: LimitReachedKind;
  limitType: LimitReachedLimitType;
  promptType: LimitPromptType;
  requiresAccountCreation: boolean;
  isAuthenticated: boolean;
  source: LimitReachedSource;
  surface?: string;
  titleKey?: string;
  messageKey?: string;
  actionKey?: string;
}

export interface SiriAuthorizationStatusResponse {
  status: SiriAuthorizationStatus;
  granted?: boolean;
}

export interface SiriAuthorizationRequestResponse {
  status: SiriAuthorizationStatus;
  granted: boolean;
}

export interface NativePlaybackStateResponse {
  state: PlaybackState;
  queue: QueueSnapshot;
}

export interface NativePlaybackPlugin {
  configure(config: NativePlaybackConfig): Promise<void>;
  setLanguage(options: NativePlaybackLanguageOptions): Promise<void>;
  clearSession(): Promise<void>;
  cue(item: PlaybackItem): Promise<void>;
  replaceQueue(options: QueueReplaceOptions): Promise<void>;
  appendToQueue(options: QueueAppendOptions): Promise<void>;
  addToQueue(options: QueueAddOptions): Promise<void>;
  removeFromQueue(options: QueueRemoveOptions): Promise<void>;
  setQueue(snapshot: QueueSnapshot): Promise<void>;
  play(): Promise<void>;
  pause(): Promise<void>;
  stop(): Promise<void>;
  seekTo(options: {positionMs: number}): Promise<void>;
  skipToNext(): Promise<void>;
  skipToPrevious(): Promise<void>;
  skipToIndex(options: {index: number}): Promise<void>;
  setVolume(options: {volume: number}): Promise<void>;
  setMuted(options: {muted: boolean}): Promise<void>;
  setPlaybackRate(options: {rate: number}): Promise<void>;
  setRepeatMode(options: {mode: RepeatMode}): Promise<void>;
  setShuffle(options: {enabled: boolean}): Promise<void>;
  getState(): Promise<NativePlaybackStateResponse>;
  getSiriAuthorizationStatus(): Promise<SiriAuthorizationStatusResponse>;
  requestSiriAuthorization(): Promise<SiriAuthorizationRequestResponse>;
  addListener(
    eventName: 'playbackStateChanged',
    listener: (event: PlaybackStateChangedEvent) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'queueChanged',
    listener: (event: QueueChangedEvent) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'remoteCommandReceived',
    listener: (event: RemoteCommandReceivedEvent) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'carConnectionChanged',
    listener: (event: CarConnectionChangedEvent) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'limitReached',
    listener: (event: LimitReachedEvent) => void,
  ): Promise<PluginListenerHandle>;
  removeAllListeners(): Promise<void>;
}

export function createEmptyQueueSnapshot(): QueueSnapshot {
  return {
    items: [],
    currentIndex: 0,
    shuffling: false,
    repeatMode: 'off',
    controlsLocked: false,
  };
}

export function createEmptyPlaybackState(): PlaybackState {
  return {
    isReady: false,
    isPlaying: false,
    isBuffering: false,
    isCurrentItemVideo: false,
    isVideoPresentationActive: false,
    currentIndex: 0,
    positionMs: 0,
    durationMs: 0,
    volume: 1,
    muted: false,
    rate: 1,
    shuffling: false,
    repeatMode: 'off',
    controlsLocked: false,
  };
}

export function createEmptySiriAuthorizationStatusResponse(): SiriAuthorizationStatusResponse {
  return {
    status: 'unknown',
    granted: false,
  };
}

export function createEmptySiriAuthorizationRequestResponse(): SiriAuthorizationRequestResponse {
  return {
    status: 'unknown',
    granted: false,
  };
}

export function isSiriAuthorizationGranted(
  response?:
    | Partial<SiriAuthorizationStatusResponse>
    | Partial<SiriAuthorizationRequestResponse>
    | null,
): boolean {
  return response?.granted === true || response?.status === 'authorized';
}
